import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from "@mui/material"
import { isAfter, isBefore } from "date-fns"
import { utcToZonedTime } from "date-fns-tz"
import React, { ChangeEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { customPalette } from "../../../../theme"
import CancelButton from "../../../components/buttons/CancelButton/CancelButton"
import { SubmitButton } from "../../../components/buttons/SubmitButton/SubmitButton"
import { Transition } from "../../../components/dialog/BaseDialog"
import { acceptedExtensions, DisplayerMode, UploadImageModal } from "../../../components/image-displayer/upload-image-modal"
import { FormDateInput } from "../../../components/inputs/date-input/FormDateInput"
import BsFileInputImage from "../../../components/inputs/file-input/bs-file-input-image"
import { FormSelectInput } from "../../../components/inputs/select-input/FormSelectInput"
import { FormTextInput } from "../../../components/inputs/text-input/FormTextInput"
import { SubscribeDialog } from "../../../components/SubscribeDialog"
import { SubtitleFormTypography } from "../../../components/typography/subtitle-form-typography"
import { SwitchLabelTypography } from "../../../components/typography/switch-label-typography"
import { TitleFormTypography } from "../../../components/typography/title-form-typography"
import { appConstants, pagesUrl } from "../../../core/appConstants"
import { DepartmentContext } from "../../../core/context/DepartmentContext"
import { OrganizationContext } from "../../../core/context/organization/organization-context"
import { CoverImgProjectContext } from "../../../core/context/project/CoverImgProjectContext"
import {
  dtoToForm,
  formToCreationDto,
  formToUpdateDto,
  IForm,
  ProjectContext,
} from "../../../core/context/project/ProjectContext"
import { Project } from "../../../core/dto/project/Project"
import { useForm } from "../../../core/hooks/form/use-form"
import { required } from "../../../core/hooks/form/validation"
import { TIME_ZONE } from "../../../core/services/date-service"
import { resolveUrl } from "../../../core/services/http-service"
import { ReEstimatingDialogContext } from "./ReEstimatingDialog/ReEstimatingDialogContext"

const fileTypeAccepted = ".png,.jpeg,.jpg"

const recomputeNeededLabel = "Cette modification va nécessiter le re-calcul des estimations pour tous les bâtiments"

function isDifferentThreshold2022(newDate: Date, previousDate: Date): boolean {
  return (
    isBefore(previousDate, utcToZonedTime("2025-01-01T00:00:00", TIME_ZONE)) &&
    isAfter(newDate, utcToZonedTime("2024-12-31T23:59:59", TIME_ZONE))
  )
}

function isDifferentThreshold2025(newDate: Date, previousDate: Date): boolean {
  return (
    isAfter(previousDate, utcToZonedTime("2024-12-31T23:59:59", TIME_ZONE)) &&
    isBefore(previousDate, utcToZonedTime("2028-01-01T00:00:00", TIME_ZONE)) &&
    (isBefore(newDate, utcToZonedTime("2025-01-01T00:00:00", TIME_ZONE)) ||
      isAfter(newDate, utcToZonedTime("2028-01-01T00:00:00", TIME_ZONE)))
  )
}

function isDifferentThreshold2028(newDate: Date, previousDate: Date): boolean {
  return (
    isAfter(previousDate, utcToZonedTime("2027-12-31T23:59:59", TIME_ZONE)) &&
    isBefore(previousDate, utcToZonedTime("2031-01-01T00:00:00", TIME_ZONE)) &&
    (isBefore(newDate, utcToZonedTime("2028-01-01T00:00:00", TIME_ZONE)) ||
      isAfter(newDate, utcToZonedTime("2031-01-01T00:00:00", TIME_ZONE)))
  )
}

function isDifferentThreshold2031(newDate: Date, previousDate: Date): boolean {
  return (
    isAfter(previousDate, utcToZonedTime("2030-12-31T23:59:59", TIME_ZONE)) &&
    isBefore(newDate, utcToZonedTime("2031-01-01T00:00:00", TIME_ZONE))
  )
}

function checkIfSameThresholdTimeBracket(newDate: Date | undefined, previousDate: Date | undefined): boolean {
  if (!previousDate || !newDate) {
    return false
  }
  if (previousDate === newDate) {
    return false
  }

  return (
    isDifferentThreshold2022(newDate, previousDate) ||
    isDifferentThreshold2025(newDate, previousDate) ||
    isDifferentThreshold2028(newDate, previousDate) ||
    isDifferentThreshold2031(newDate, previousDate)
  )
}

interface IProps {
  open: boolean
  handleClose(): void
}

export function ProjectForm({ open, handleClose }: Readonly<IProps>): React.JSX.Element {
  const navigate = useNavigate()

  const { refreshOrganization } = useContext(OrganizationContext)
  const { project, createNewProject, updateActualProject } = useContext(ProjectContext)
  const { file, createOrUpdateCoverImgProject, deleteCoverImgProject, fetchCoverImgProject } =
    useContext(CoverImgProjectContext)
  const { departmentsList } = useContext(DepartmentContext)
  const { handleOpen } = useContext(ReEstimatingDialogContext)

  const [imageTextError, setImageTextError] = useState<string>()
  const [openImageModal, setOpenImageModal] = useState<boolean>(false)
  const [tmpFile, setTmpFile] = useState<File | undefined>(file)
  const [openSubscription, setOpenSubscription] = useState<boolean>(false)

  const validateImage = useRef<boolean>(false)

  useEffect(() => {
    setTmpFile(file)
  }, [file])

  useEffect(() => {
    if (open && project?.coverImgFileName && project?.id && project?.coverImgFileName) {
      fetchCoverImgProject(project.id, project?.coverImgFileName)
    }
  }, [open, project?.id, project, fetchCoverImgProject])

  const asynchronousUpdateCoverImg: (_project: Project | undefined) => void = useCallback(
    (_project) => {
      if (_project?.id && tmpFile) {
        createOrUpdateCoverImgProject(_project?.id, tmpFile)
      } else {
        deleteCoverImgProject()
      }
    },
    [createOrUpdateCoverImgProject, deleteCoverImgProject, tmpFile]
  )

  const submit: (form: IForm) => Promise<any> = useCallback(
    (form) => {
      form.name = form.name.trim()
      if (project?.id) {
        return updateActualProject(formToUpdateDto(project, form))
          .then(() => {
            asynchronousUpdateCoverImg(project)
          })
          .then(() => {
            handleClose()
            refreshOrganization() // Asynchronously update so licence numbers are update
          })
      } else {
        return createNewProject(formToCreationDto(form))
          .then((createdProject: Project) => {
            asynchronousUpdateCoverImg(createdProject)
            return createdProject
          })
          .then((createdProject) => {
            handleClose()
            refreshOrganization() // Asynchronously update so licence numbers are update
            navigate(resolveUrl(pagesUrl.PROJECT_PAGE_DETAIL, [createdProject?.id]))
          })
      }
    },
    [project, updateActualProject, handleClose, refreshOrganization, createNewProject, navigate, asynchronousUpdateCoverImg]
  )

  const { form, errors, handleChange, handleSubmit, resetForm, isSubmitting } = useForm(
    project,
    dtoToForm,
    [
      required("name"),
      required("businessCode"),
      required("street"),
      required("city"),
      required("departmentId"),
      required("licenceDate"),
    ],
    submit
  )

  const isDateModified = useMemo(
    () => checkIfSameThresholdTimeBracket(form?.licenceDate, project?.licenceDate),
    [form.licenceDate, project]
  )

  function handleChangeDate(date: Date): void {
    if (!date) {
      return
    }
    // It's when handleChange expects ChangeEvent
    const e = {
      target: {
        id: "licenceDate",
        value: date,
      },
    } as unknown as ChangeEvent<HTMLInputElement>

    handleChange(e)
  }

  const onCancel: () => void = useCallback(() => {
    handleClose()
    resetForm()
    setTmpFile(undefined)
  }, [handleClose, resetForm])

  function onChange(coverImageFile: File): void {
    const image = new Image()
    image.src = URL.createObjectURL(coverImageFile)
    image.onload = () => {
      const width = image.width
      const height = image.height
      validateImage.current = validateCoverImage(coverImageFile.size, height, width, coverImageFile.name)
      if (validateImage.current) {
        setTmpFile(coverImageFile)
      }
    }
    setOpenImageModal(true)
  }

  function validateCoverImage(size: number, height: number, width: number, filename: string): boolean {
    if (size > appConstants.miscellaneous.PROJECT_IMG_MAX_SIZE * 1024 * 1024) {
      setImageTextError(
        `Le fichier est trop volumineux. La taille maximale est de ${appConstants.miscellaneous.PROJECT_IMG_MAX_HEIGTH} Mo.`
      )
      return false
    } else if (
      height > appConstants.miscellaneous.PROJECT_IMG_MAX_HEIGTH ||
      width > appConstants.miscellaneous.PROJECT_IMG_MAX_WIDTH
    ) {
      setImageTextError(
        `Le fichier est trop volumineux. La taille maximale est de ${appConstants.miscellaneous.PROJECT_IMG_MAX_HEIGTH} x ${appConstants.miscellaneous.PROJECT_IMG_MAX_HEIGTH}.`
      )
      return false
    } else if (!acceptedExtensions.includes(filename.toLowerCase().substring(filename.lastIndexOf(".")))) {
      setImageTextError(`Le format du fichier n'est pas valide. Voici les extensions acceptés: ${fileTypeAccepted}`)
      return false
    } else {
      return true
    }
  }

  function deleteImage(): void {
    setOpenImageModal(false)
    setTmpFile(undefined)
  }

  function cancelUpload(): void {
    setTmpFile(file)
    setOpenImageModal(false)
  }

  function handleCloseModal(): void {
    setOpenImageModal(false)
  }

  function onChangeSwitch(event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void {
    const e: ChangeEvent<HTMLInputElement> = {
      target: {
        id: event.target.id,
        checked,
        type: "checkbox",
      },
    } as ChangeEvent<HTMLInputElement>
    handleChange(e)
  }

  return (
    <>
      <Dialog open={open} maxWidth="lg" fullWidth onClose={onCancel} TransitionComponent={Transition}>
        <Box component="form" id="create-project-form" onSubmit={(e) => handleSubmit(e, false)}>
          <UploadImageModal
            file={tmpFile}
            saveFile={() => {
              setOpenImageModal(false)
            }}
            cancelUpload={cancelUpload}
            validateImage={validateImage.current}
            imageTextError={imageTextError}
            fileType={fileTypeAccepted}
            open={openImageModal}
            handleClose={handleCloseModal}
            imageTitle="Valider la photo de couverture"
            handleChangeFile={(event) => {
              if (event?.target?.files && event.target.files[0]) {
                onChange(event.target.files[0])
                event.target.value = ""
              }
            }}
            isloading
            displayMode={DisplayerMode.SAVE}
          />
          <DialogTitle>
            {project?.id ? (
              <TitleFormTypography label="Mettre à jour le projet" />
            ) : (
              <TitleFormTypography label="Nouveau projet" />
            )}
          </DialogTitle>
          <DialogContent sx={{ pb: 0 }}>
            <Grid container columnSpacing={3}>
              <Grid item xs={3}>
                <BsFileInputImage
                  onChange={onChange}
                  cancelUpload={cancelUpload}
                  deleteImage={deleteImage}
                  file={tmpFile}
                  disabled={false}
                  fileType={fileTypeAccepted}
                />
                <Box pt={1}>
                  <Typography sx={{ color: customPalette.lightGray3, fontSize: "12px" }}>
                    Les champs précédés d'un astérisque sont obligatoires.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={9} container>
                <Grid item xs={12} container columnSpacing={1}>
                  <Grid item xs={12}>
                    <SubtitleFormTypography label="Informations" />
                  </Grid>
                  <Grid item xs={12}>
                    <FormTextInput fieldName="name" label="Nom" handleChange={handleChange} form={form} errors={errors} />
                  </Grid>
                  <Grid item xs={6}>
                    <FormTextInput
                      fieldName="businessCode"
                      label="Numéro d'affaire"
                      handleChange={handleChange}
                      form={form}
                      errors={errors}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormDateInput
                      label="Date de dépôt de permis"
                      value={form?.licenceDate}
                      handleChange={handleChangeDate}
                      views={["year", "month", "day"]}
                      hideButton
                      errorText={errors.licenceDate}
                      helperText={isDateModified ? recomputeNeededLabel : undefined}
                      disablePast={false}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12} container columnSpacing={1}>
                  <Grid item xs={12}>
                    <SubtitleFormTypography label="Localisation" />

                    <FormTextInput
                      fieldName="street"
                      label="Adresse"
                      handleChange={handleChange}
                      form={form}
                      errors={errors}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormTextInput
                      fieldName="additional"
                      label="Complément d'adresse"
                      handleChange={handleChange}
                      form={form}
                      errors={errors}
                      required={false}
                    />
                  </Grid>

                  <Grid item xs={8}>
                    <FormTextInput fieldName="city" label="Ville" handleChange={handleChange} form={form} errors={errors} />
                  </Grid>

                  <Grid item xs={4}>
                    <FormSelectInput
                      id="departmentId"
                      label="Département"
                      mode="event"
                      handleEventChange={handleChange}
                      options={departmentsList.map((dept) => ({
                        value: dept.departmentId,
                        label: `${dept.departmentNumber} - ${dept.departmentName}`,
                      }))}
                      selectedOption={form.departmentId}
                      errors={errors}
                      nullable
                    />
                  </Grid>

                  {false && (
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={<Switch id="seismicZone" checked={form.seismicZone} onChange={onChangeSwitch} />}
                        label={<SwitchLabelTypography label="Le projet est en zone sismique" />}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <CancelButton onClick={onCancel} label="Annuler" />
            {project?.id ? (
              <SubmitButton label="Mettre à jour" formId="create-project-form" isSubmitting={isSubmitting} />
            ) : (
              <SubmitButton label="Créer" formId="create-project-form" isSubmitting={isSubmitting} />
            )}
          </DialogActions>
        </Box>
      </Dialog>

      <SubscribeDialog handleClose={() => setOpenSubscription(false)} isOpen={openSubscription} />
    </>
  )
}
